import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { APP_CONTENT_ID } from '@core/layouts/appShell';
import { Button } from './Button';

export interface ModalProps {
  /** Whether the modal is shown. */
  open: boolean;
  /**
   * Called when the user dismisses (close button, backdrop, or Escape). With
   * `dirty`, those three ask first and only call it once the user discards.
   */
  onClose: () => void;
  /** Accessible dialog title, rendered in the header. */
  title: string;
  /** Modal body. */
  children: ReactNode;
  /**
   * Optional footer, typically the action buttons.
   *
   * Enter activates the **primary** action: the control marked
   * `data-modal-primary`, or failing that the last enabled button here, which
   * matches the design system's order (cancel first, primary last). A primary
   * action rendered as `Button variant="danger"` is exempt: destructive steps
   * are never a keystroke away.
   */
  footer?: ReactNode;
  /** Max width. Defaults to `md`. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The dialog holds work the user would lose: something typed or chosen that
   * differs from what it opened on. Escape, a backdrop click and × then ask
   * "discard changes?" inside the dialog instead of closing. A footer Cancel
   * button stays a deliberate close and is not asked about.
   */
  dirty?: boolean;
  /** A request is in flight: Escape, the backdrop and × do nothing until it ends. */
  busy?: boolean;
}

const MAX_WIDTH: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-5xl',
};

/** Tabbable elements inside the panel, in DOM order (for the focus trap). */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Controls where Enter means "write a newline", not "confirm". */
const EDITS_TEXT = ['textarea'];

/**
 * Would the browser submit a form by itself because Enter was pressed here?
 *
 * A text input inside a form that has a submit control triggers implicit
 * submission. Firing the dialog's default action as well would send it twice.
 */
function nativeSubmitWillFire(target: HTMLElement): boolean {
  const form = (target as HTMLInputElement).form;
  if (!form || target.tagName.toLowerCase() !== 'input') {
    return false;
  }
  return Array.from(form.elements).some(
    (element) => (element as HTMLInputElement).type === 'submit' && !(element as HTMLInputElement).disabled,
  );
}

/**
 * Accessible, brand-styled modal dialog (React-controlled rather than Preline's
 * data attributes, so its open state is never out of sync with React's).
 * Centered on `sm+`, a bottom sheet on mobile. Closes on the × button, a
 * backdrop click, or Escape, and Enter presses the primary action; locks body
 * scroll and traps Tab while open.
 * Compose `@core/ui` primitives in `children` / `footer`.
 *
 * It renders in a portal on `document.body` and marks the app shell
 * (`APP_CONTENT_ID`) `inert` while open, so the page behind it is genuinely
 * unreachable — not merely hidden from `aria-modal`. Initial focus goes to the
 * first footer control (typically "Cancel"); on close, focus returns to the
 * element that opened the dialog (WCAG 2.4.3).
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  dirty = false,
  busy = false,
}: ModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  // Read through a ref so an inline `onClose` arrow does not re-run the effect
  // (which would drop `inert` and bounce focus on every parent render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  // The "discard changes?" step, shown in place of the footer. The focus it took
  // from the form goes back there when the user keeps editing.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const confirmingRef = useRef(false);
  confirmingRef.current = confirmingDiscard;
  const focusBeforeDiscardRef = useRef<HTMLElement | null>(null);
  // A backdrop click counts only when the press started on the backdrop too, so
  // a text selection dragged out of the panel never closes the dialog.
  const pressedBackdropRef = useRef(false);
  const escapeClosesListRef = useRef(false);
  // Who opened us, captured while rendering: by the time effects run, a field with
  // `autoFocus` has already taken the focus, and reading it then would name the
  // field as its own opener and lose the way back (WCAG 2.4.3).
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpen.current = open;
  if (!open && confirmingDiscard) {
    setConfirmingDiscard(false);
  }

  /**
   * Escape, the backdrop and × come here: the accidental ways out. A dialog with
   * nothing to lose closes at once; one holding work asks first.
   */
  const requestClose = () => {
    if (busyRef.current || confirmingRef.current) {
      return;
    }
    if (dirtyRef.current) {
      focusBeforeDiscardRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setConfirmingDiscard(true);
      return;
    }
    onCloseRef.current();
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  const keepEditing = () => {
    setConfirmingDiscard(false);
    const previous = focusBeforeDiscardRef.current;
    if (previous?.isConnected) {
      // After React puts the original footer back.
      queueMicrotask(() => previous.focus());
    }
  };
  const keepEditingFnRef = useRef(keepEditing);
  keepEditingFnRef.current = keepEditing;

  useEffect(() => {
    if (confirmingDiscard) {
      keepEditingRef.current?.focus();
    }
  }, [confirmingDiscard]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const opener = openerRef.current;
    const shell = document.getElementById(APP_CONTENT_ID);
    const shellWasInert = shell?.hasAttribute('inert') ?? false;
    shell?.setAttribute('inert', '');

    const getFocusable = (): HTMLElement[] => {
      const panel = panelRef.current;
      if (!panel) {
        return [];
      }
      return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    };

    /**
     * Enter confirms, the way a native dialog behaves: it presses the default
     * action whichever control has focus, because Space is the key that presses
     * the focused button. Typing keeps its meaning, though: a newline in a
     * textarea, and a form that submits itself is left to do it. A `danger`
     * action is never pressed by Enter at all.
     *
     * Escape is handled above, so the pair reads the way people expect: Enter
     * confirms, Escape backs out.
     */
    const handleEnter = (event: KeyboardEvent) => {
      if (event.isComposing || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target) {
        if (EDITS_TEXT.includes(target.tagName.toLowerCase()) || target.isContentEditable) {
          return;
        }
        if (nativeSubmitWillFire(target)) {
          return;
        }
        // A combobox with its list open (a searchable select) takes Enter to pick
        // the option; the dialog's default action waits for the next Enter.
        if (target.getAttribute('role') === 'combobox' && target.getAttribute('aria-expanded') === 'true') {
          return;
        }
      }
      if (event.defaultPrevented) {
        return;
      }
      const primary = primaryAction();
      if (!primary || target === primary) {
        // No default action, or the browser is about to press it anyway.
        return;
      }
      if (primary.dataset.variant === 'danger') {
        // Destructive dialogs have no keyboard default: deleting, archiving or
        // ending a session must be a deliberate click (or Space on the focused
        // button). Escape still backs out.
        return;
      }
      // Stops the focused control from being activated as well: Enter belongs to
      // the default action, Space to whatever has focus.
      event.preventDefault();
      primary.click();
    };

    /** The dialog's default action: marked as primary, or the last enabled button. */
    const primaryAction = (): HTMLElement | null => {
      const footerElement = footerRef.current;
      if (!footerElement) {
        return null;
      }
      const marked = footerElement.querySelector<HTMLElement>('[data-modal-primary]:not([disabled])');
      if (marked) {
        return marked;
      }
      const buttons = Array.from(footerElement.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
      return buttons.length > 0 ? buttons[buttons.length - 1] : null;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (escapeClosesListRef.current) {
          escapeClosesListRef.current = false;
          return;
        }
        if (confirmingRef.current) {
          keepEditingFnRef.current();
        } else {
          requestCloseRef.current();
        }
        return;
      }
      if (event.key === 'Enter') {
        handleEnter(event);
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const panel = panelRef.current;
      if (!panel) {
        return;
      }
      const focusable = getFocusable();
      const active = document.activeElement as HTMLElement | null;
      if (focusable.length === 0) {
        // Nothing tabbable inside: keep focus on the panel itself.
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusInside = active !== null && panel.contains(active);
      if (event.shiftKey) {
        if (!focusInside || active === first || active === panel) {
          event.preventDefault();
          last.focus();
        }
      } else if (!focusInside || active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // A searchable select with its list open takes Escape to close the list. It is
    // read on the way down: by the time the key bubbles here the list is closed.
    const noteListEscape = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      escapeClosesListRef.current =
        event.key === 'Escape' &&
        target?.getAttribute('role') === 'combobox' &&
        target.getAttribute('aria-expanded') === 'true';
    };

    document.addEventListener('keydown', noteListEscape, true);
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // A field that asked for the focus already has it by now (React applies
    // `autoFocus` before effects run), and a dialog built around one input should
    // be ready to type in, so leave it alone. Otherwise the first footer control
    // (Cancel, by the design system's order); with no footer, the panel itself.
    const active = document.activeElement;
    const alreadyInside = active instanceof HTMLElement && panelRef.current?.contains(active);
    if (!alreadyInside) {
      const firstAction = footerRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (firstAction ?? panelRef.current)?.focus();
    }
    return () => {
      document.removeEventListener('keydown', noteListEscape, true);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (!shellWasInert) {
        shell?.removeAttribute('inert');
      }
      if (opener?.isConnected) {
        opener.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[1px]"
        onMouseDown={(event) => {
          pressedBackdropRef.current = event.target === event.currentTarget;
        }}
        onClick={(event) => {
          const pressedHere = pressedBackdropRef.current;
          pressedBackdropRef.current = false;
          if (pressedHere && event.target === event.currentTarget) {
            requestClose();
          }
        }}
        aria-hidden="true"
        data-testid="modal-backdrop"
      />
      {/* The dialog is the panel, not the full-screen wrapper: the backdrop is
          decoration and must stay outside the element that names the dialog. */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // The bottom inset applies below `sm`, where this is a bottom sheet
        // sitting on the home indicator; centered dialogs above `sm` clear it
        // anyway, and the inset is 0 on the web.
        className={`relative flex max-h-[92dvh] w-full flex-col rounded-t-xl border border-[var(--color-border)] bg-[var(--color-surface)] pb-[env(safe-area-inset-bottom)] shadow-sm focus:outline-hidden sm:rounded-xl sm:pb-0 ${MAX_WIDTH[size]}`}
      >
        <header className="flex shrink-0 items-center justify-between gap-x-3 border-b border-[var(--color-border)] px-5 py-4">
          <h2 className="text-base font-semibold text-[var(--color-text)]">{title}</h2>
          <button
            type="button"
            onClick={requestClose}
            aria-label={t('common.close')}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text)] focus:outline-hidden focus:ring-2 focus:ring-[var(--color-accent)]"
          >
            <Icon icon="heroicons:x-mark" className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-4">{children}</div>

        {confirmingDiscard ? (
          <footer
            ref={footerRef}
            className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[var(--color-border)] bg-[var(--color-bg-muted)] px-5 py-4"
          >
            <p role="alert" className="w-full text-sm sm:mr-auto sm:w-auto font-medium text-[var(--color-text)]">
              {t('common.unsavedChanges')}
            </p>
            <Button variant="danger" size="sm" onClick={() => onCloseRef.current()}>
              {t('common.discardChanges')}
            </Button>
            <Button ref={keepEditingRef} data-modal-primary size="sm" onClick={keepEditing}>
              {t('common.keepEditing')}
            </Button>
          </footer>
        ) : (
          footer && (
            <footer
              ref={footerRef}
              className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-[var(--color-border)] px-5 py-4"
            >
              {footer}
            </footer>
          )
        )}
      </div>
    </div>,
    document.body,
  );
}
